import { css, html } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";
import { DefaultPage } from "./RootComponent";
import { InventoryService } from "../services/InventoryService";

type Item = {
    name: string;
    image: string;
};
/** CSS affecting the {@link InventoryComponent} */
const styles: string = css`
    :host {
        font-family: "Onesize";
        width: 100%;
        max-width: 1024px;
        height: 100%;
        display: grid;
        grid-template-columns: 1fr;
        grid-template-rows: auto calc(35vh + 10px) minmax(calc(35vh + 10px), 1fr) auto;
        grid-column-gap: 0px;
        grid-row-gap: 0px;
        z-index: 3;
    }

    .title {
        text-align: center;
        margin-top: 10px;
        overflow: auto;
    }

    .header {
        display: flex;
        flex-direction: column;
        align-items: center;
        position: relative;
        margin-top: 10px;
    }

    .header img {
        width: 100%;
        height: 100%;
        image-rendering: pixelated;
    }

    .header img:nth-child(n + 2) {
        position: absolute;
    }

    .content {
        flex-grow: 1;
        overflow: auto;
        margin-top: 10px;
        padding: 0 10px;
    }

    .content p {
        margin: 0 0 10px 0;
    }

    .content p:last-of-type {
        margin: 0;
    }

    .footer {
        border-radius: 10px 10px 0 0;
        background-color: #52478b;
        border: 1px solid #332c57;
        margin-top: 10px;
        display: flex;
        height: 105px;
    }

    .footer .buttons {
        display: flex;
        flex-direction: column;
        overflow: auto;
        padding: 10px 10px 0 10px;
    }

    .footer .button {
        background-color: #7f6ed7;
        border: 1px solid #332c57;
        padding: 5px 10px;
        margin: 0 0 10px 10px;
        text-transform: uppercase;
        cursor: pointer;
        display: inline-block;
        user-select: none;
    }

    .footer .button.active,
    .footer .button:hover {
        background-color: #332c57;
    }

    /* Inventory Styles */
    .inventory-container {
        width: 95%;
        max-width: 1200px;
        margin: 50px auto;
        background: #52478b; /* Purple background similar to footer */
        padding: 45px;
        border-radius: 10px;
        box-shadow: 0 0 10px rgba(0, 0, 0, 0.5);
        color: #ffffff; /* Keep text color consistent */
        font-family: "Onesize"; /* Matching font-family */
        text-align: center;
        border: 1px solid #332c57; /* Dark border similar to footer */
    }

    .inventory-title {
        font-size: 24px;
        margin-bottom: 30px;
        color: #ffffff; /* Light text color */
    }

    .inventory-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr); /* 4 columns */
    gap: 15px; /* Consistent spacing between items */
    padding: 10px 200px;
    justify-content: center; /* Centers grid as a whole if it's smaller than the container */
    justify-items: center;   /* Centers each item within its grid cell */
    align-items: center;     /* Centers items vertically in each cell */
    margin: 0 auto;          /* Ensure the grid is centered in the container */
}


    .inventory-slot {
        position: relative;
        width: 100px;
        height: 120px; /* Increased height for description area */
        background: #7f6ed7; /* Light purple background for slots */
        border: 4px solid #332c57; /* Dark border around each slot */
        border-radius: 10px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-start; /* Start items from the top */
        overflow: hidden;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
        transition: transform 0.2s ease-in-out;
        padding: 0;
    }

    .inventory-slot:hover {
        transform: scale(1.05);
        background: #332c57; /* Darken background on hover */
    }

    /* Updated inventory-text to always be on top */
    .inventory-text {
        width: 100%;
        height: 13%; /* Fixed height for text */
        background: rgba(0, 0, 0, 0.6); /* Dark semi-transparent background */
        color: white;
        font-size: 12px;
        text-align: center;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 2px;
        position: absolute;
        top: 0; /* Move text to the top */
        left: 0;
    }

    /* Ensure the image is positioned below the text */
    .inventory-slot img {
        width: 100%;
        height: 100%; /* Image takes up remaining space */
        object-fit: cover;
        margin-top: 20%; /* Pushes the image down to make space for text */
    }

    .item-count {
        position: absolute;
        bottom: 8px;
        right: 8px;
        background: rgba(0, 0, 0, 0.8);
        color: white;
        font-size: 14px;
        padding: 3px 6px;
        border-radius: 5px;
    }

    /* Back button for inventory */
    .back-button {
        background-color: #7f6ed7; /* Same background color as other buttons */
        border: 1px solid #332c57; /* Dark border to match the game's style */
        padding: 10px 15px; /* Same padding as other buttons */
        margin: 10px ; /* Consistent margin */
        text-transform: uppercase; /* Same text-transform as in the game's buttons */
        cursor: pointer;
        display: inline-block;
        user-select: none;
        font-family: "Onesize"; /* Same font as other buttons */
        font-size: 20px; /* Adjusted font size to match other buttons */
        color: white;
    }

    .back-button:hover {
        background: #332c57; /* Darker background on hover to match the game's buttons */
        transform: scale(1.05); /* Slight scale effect on hover */
    }
`;

/**
 * Represents the inventory
 */
export class InventoryComponent extends HTMLElement {
    /** Instance of the game event service */
    private readonly _gameEventService: GameEventService = new GameEventService();
    private readonly _inventoryService: InventoryService = new InventoryService();
    /** Current game session */
    private _currentInventory: Item[] = [];

    private async refreshGameState(): Promise<void> {
        const inventory: string[] = await this._inventoryService.getInventory();

        // Ensure inventory follows the correct format

        inventory.map((item: string) => {
            this._currentInventory.push({
                name: item,
                image: `assets/img/rooms/${item.toLowerCase().replace(/\s/g, "_")}.png`,
            });
        });

        // Refresh the web component
        this.render();
    }

    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });
        void this.refreshGameState();
    }

    /**
     * Render the contents of this page
     */
    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        const element: HTMLElement = this.renderInventory();

        this.shadowRoot.firstChild?.remove();
        this.shadowRoot.append(element);
    }

    /**
     * Render the Inventory page
     * here i will create the html for my inventory (looks)
     *
     * @returns HTML element of the Inventory page
     */
    private renderInventory(): HTMLElement {
        const totalSlots: number = 12; // Update to 32 slots (8x4 grid)
        const inventoryItems: { name: string; image: string }[] = this._currentInventory;
        const emptySlots: number = totalSlots - inventoryItems.length; // Explicitly typed

        const element: HTMLElement = html`
        <div class="inventory-container">
            <style>${styles}</style>
            <h1 class="inventory-title">Inventory</h1>
            <div class="inventory-grid">
                ${inventoryItems.map(item => `
                    <div class="inventory-slot">
                        <img src="${item.image || "assets/default.png"}" alt="${item.name}" onerror="this.onerror=null; this.src='assets/default.png';">
                        <div class="inventory-text">${item.name}</div> <!-- Description area -->
                    </div>
                `).join("")}
                ${Array.from({ length: emptySlots }, () => `
                    <div class="inventory-slot">
                        <div class="inventory-text">Empty Slot</div> <!-- Placeholder text for empty slots -->
                    </div>
                `).join("")}
            </div>
            <button id="back-to-home" class="back-button">Terug naar de game</button>
        </div>
    `;

        element.querySelector("#back-to-home")?.addEventListener("click", e => {
            e.preventDefault();
            this._gameEventService.switchPage(DefaultPage);
        });

        return element;
    }
}
